'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { VoucherPriceFragmentFragmentDoc } from '@packages/gql/generated/shopping/VoucherPriceFragmentFragmentDoc';
import { useSearchParams } from 'next/navigation';
import { useIntl } from 'react-intl';
import { unmask } from '@packages/gql/src/betterMasking';
import { usePriceFormater } from '../../hooks/usePriceFormater';
import { getVoucherPriceDiscountPercent } from './getVoucherPriceDiscountPercent/getVoucherPriceDiscountPercent';

/* GraphQL */ `
  fragment VoucherPriceFragment on ProductPrice {
    currency
    value
  }
`;

export type VoucherPriceProps = {
  data: FragmentType<typeof VoucherPriceFragmentFragmentDoc>;
};

/**
 * <p>This component renders a voucher price, also called "Geizhals Einsprung".</p>
 * */
export const VoucherPrice = ({ data }: VoucherPriceProps) => {
  const { value, currency } = unmask<typeof VoucherPriceFragmentFragmentDoc>(data);
  const searchParams = useSearchParams();
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('PRODUCT');

  const voucherPriceDiscount = searchParams ? getVoucherPriceDiscountPercent(searchParams) : null;

  // explicit null is needed here because voucherPriceDiscount can be "0"
  if (voucherPriceDiscount === null) {
    return null;
  }

  return (
    <div>
      <div className="text-size-h4 text-color-sale font-bold">
        {formatMessage(
          {
            id: 'voucherprice.discount.pricetext',
            defaultMessage: '{price} mit Ihrem Rabatt',
          },
          {
            price: formatPrice(Math.round(value * ((100 - voucherPriceDiscount) / 100)), currency),
          },
        )}
      </div>
      <div className="text-size-body3 text-color-text-darktransparent">
        {formatMessage({
          id: 'voucherprice.discount.subtext',
          defaultMessage: 'Ihr Rabatt wird im letzten Bestellschritt automatisch abgezogen.',
        })}
      </div>
    </div>
  );
};
